
import React, { useState, useEffect } from "react";
import DatePicker from "../../../components/Custom/Forms/DatePicker/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Button, Form } from "reactstrap";
import {
    get_daily_turnover,
} from "../../../redux/slices/payinPayoutSlice";
import { Filter_Data_Transaction } from "../../../redux/slices/transactionSlice";
import ControlledDropDown from "../../../components/Custom/Forms/Controller/ControlledDropDown";
import {
    dateFormatter,
    getCurrentMonthDates,
    seperator,
} from "../../../helperFunctions";
import { DROPDOWN_ALL_VALUE } from "../../../constant";
import Loader from "../../../components/Custom/Loader";

const DailyTurnover = () => {
    const dispatch = useDispatch();
    const { startOfMonth, endOfMonth } = getCurrentMonthDates();

    const { getDailyTurnoverLoading } = useSelector((state) => state.payinPayout);
    const { transactionFilterState } = useSelector((state) => state.transaction);

    const [selectedRange, setSelectedRange] = useState([startOfMonth, endOfMonth]);

    const initialValues = {
        date: [startOfMonth, endOfMonth],
        merchantId: DROPDOWN_ALL_VALUE,
    };

    const { control, handleSubmit, reset, setValue } = useForm({
        defaultValues: initialValues,
    });

    const handleDateChange = ([newStartDate]) => {
        const base = newStartDate || new Date();

        const start = new Date(base.getFullYear(), base.getMonth(), 1);
        let end = new Date(base.getFullYear(), base.getMonth() + 1, 0);

        if (end > new Date()) end = new Date();

        setSelectedRange([start, end]);
        setValue("date", [start, end]);
    };

    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: "area",
                stacked: true,
                stackType: "normal",
                toolbar: { show: false },
                zoom: { enabled: false },
            },

            dataLabels: { enabled: false },

            stroke: {
                curve: "smooth",
                width: 2,
            },

            fill: {
                type: "gradient",
                gradient: {
                    opacityFrom: 0.9,
                    opacityTo: 0.6,
                },
            },

            xaxis: {
                type: "datetime",
                labels: {
                    show: true,
                    rotate: -45,
                },
            },

            yaxis: {
                labels: {
                    formatter: (val) => seperator(val, false),
                },
                title: { text: "Amount" },
            },

            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (val) => seperator(val),
                },
            },

            legend: {
                position: "top",
                horizontalAlign: "left",
            },

            grid: {
                borderColor: "#f1f1f1",
            },

            colors: [],
        },
    });

    useEffect(() => {
        dispatch(Filter_Data_Transaction());
    }, []);

    const getMerchantOptions = () =>
        transactionFilterState?.cilent_data
            ?.slice()
            ?.sort((a, b) => a?.merchantName?.localeCompare(b?.merchantName))
            ?.map((v) => ({
                value: v?.merchantId,
                label: v?.merchantName,
            })) || [];

    const fetchDailyTurnover = async (data = initialValues) => {
        try {
            const payload = {
                startDate: dateFormatter(data.date?.[0] || startOfMonth, "start"),
                endDate: dateFormatter(data.date?.[1] || endOfMonth, "end"),
                filter_data: {
                    merchantId:
                        data.merchantId === DROPDOWN_ALL_VALUE ? "all" : data.merchantId,
                },
            };

            const res = await dispatch(get_daily_turnover(payload)).unwrap();

            if (res?.data) {
                const labels = (res.data.labels || []).map(
                    d => `${payload.startDate.slice(0, 7)}-${d}`
                );
                const datasets = res.data.datasets || [];

                const series = datasets.map((ds) => ({
                    name: ds.label,
                    data: ds.data.map((v, i) => ({
                        x: labels[i],
                        y: v,
                    })),
                }));

                const colors = datasets.map((d) => d.backgroundColor);

                setChartData((prev) => ({
                    ...prev,
                    series,
                    options: {
                        ...prev.options,
                        xaxis: { ...prev.options.xaxis },
                        colors,
                    },
                }));
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchDailyTurnover();
    }, []);

    const onSubmit = (data) => fetchDailyTurnover(data);
    return (
        <div className="page-content">
            <Card>
                <CardHeader className="d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-start">
                    <div>
                        <h4 className="main-title"><b>Daily Turnover</b></h4>
                    </div>
                    <Form
                        onSubmit={handleSubmit(onSubmit)}
                        className="d-flex align-items-end inputgap"
                    >
                        <ControlledDropDown
                            control={control}
                            name="merchantId"
                            label="Client"
                            options={[
                                { label: "All", value: DROPDOWN_ALL_VALUE },
                                ...getMerchantOptions()
                            ]}
                        />
                        <DatePicker
                            selected={selectedRange[0]}
                            startDate={selectedRange[0]}
                            endDate={selectedRange[1]}
                            maxDate={new Date()}
                            selectsRange
                            isKeyDown={false}
                            onChange={handleDateChange}
                            showMonthYearPicker
                        />
                        <Button color="primary" type="submit">
                            Apply
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            onClick={() => {
                                reset(initialValues);
                                setSelectedRange([startOfMonth, endOfMonth]);
                                fetchDailyTurnover(initialValues);
                            }}
                        >
                            Reset
                        </Button>
                    </Form>
                </CardHeader>
                <CardBody>


                    {getDailyTurnoverLoading ? (
                        <div
                            style={{
                                height: 400,
                            }}
                            className="flex-center"
                        >
                            <Loader />
                        </div>
                    ) : (
                        <div id="chart">
                            <ReactApexChart
                                options={chartData.options}
                                series={chartData.series}
                                type="area"
                                height={350}
                            />
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default DailyTurnover;
